import { useEffect, useRef, useState } from 'react';
import { HomeScreen } from './screens/Home';
import { HostScreen } from './screens/HostScreen';
import { HostEditor } from './screens/HostEditor';
import { Dashboard } from './screens/Dashboard';
import { SessionHistory } from './screens/SessionHistory';
import { PlayerScreen } from './screens/PlayerScreen';
import { About } from './screens/About';
import { Admin } from './screens/Admin';
import { Games } from './screens/Games';
import { Login } from './screens/Login';
import { Register } from './screens/Register';
import { Forgot } from './screens/Forgot';
import { Reset } from './screens/Reset';
import { Verify } from './screens/Verify';
import { Privacy } from './screens/Privacy';
import { Link } from './components/ui';
import { UnverifiedBanner } from './components/UnverifiedBanner';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { ThemeToggle } from './components/ThemeToggle';
import { A11yPanel } from './components/A11yPanel';
import { isKnownRoute, matchOpenRoute, navigate, redirect, useRoute } from './router';
import { OpenQuizRun } from './screens/OpenQuizRun';
import { AuthProvider, useAuth } from './useAuth';
import { useTranslation } from 'react-i18next';
import { PIN_LENGTH } from './util';
import { loadHost, loadPlayer } from './activeSession';

/**
 * Корневой компонент kikayoo — диспетчер History-маршрутов (И6) поверх локальной
 * state-машины потоков ведущего/игрока на главной.
 *
 * Маршруты (см. router.ts):
 * - `/`        → главная (мгновенный вход в квиз: PIN + «Вести игру»). `?pin=`
 *               (QR-вход) сразу открывает экран ввода ника игрока.
 * - `/about`   → об авторе + донаты (отдельная, без шумной навигации).
 * - `/games`   → раздел мини-игр (без ссылки в основном меню).
 * - `/edit`    → редактор квизов (И5). Требует входа (гейт И3): без аккаунта —
 *               редирект на `/login` (редактор сохраняет черновики и скоро будет
 *               привязывать квизы к профилю, И4).
 * - `/login`, `/register`, `/forgot`, `/reset` → auth-экраны (И3).
 * - неизвестный → редирект на `/` (серверный SPA-fallback уже отдал index.html).
 *
 * Подпотоки `host`/`player` остаются state-машиной внутри (PIN в URL не нужен).
 *
 * Auth-состояние (залогиненность) поднимается на верхний уровень через
 * [`AuthProvider`](./useAuth.tsx): bootstrap `GET /api/auth/me` при старте определяет
 * текущего пользователя, чтобы главная и гейт защищённых роутов знали о нём.
 */

type Screen = { kind: 'home' } | { kind: 'host' } | { kind: 'player'; pin: string };

/** Извлекает и санитайзит PIN из query (?pin=) для бесшовного входа по QR. */
function readPinFromQuery(): string {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') return '';
  const raw = new URLSearchParams(window.location.search).get('pin') ?? '';
  return raw.replace(/\D/g, '').slice(0, PIN_LENGTH);
}

/** Извлекает токен восстановления пароля из query (?token=) для /reset. */
function readResetToken(): string | undefined {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') return undefined;
  const raw = new URLSearchParams(window.location.search).get('token');
  return raw && raw.length > 0 ? raw : undefined;
}

/** Извлекает токен подтверждения email из query (?token=) для /verify. */
function readVerifyToken(): string | undefined {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') return undefined;
  const raw = new URLSearchParams(window.location.search).get('token');
  return raw && raw.length > 0 ? raw : undefined;
}

/**
 * Извлекает номер страницы истории сессий из query (?page=) для /me/history
 * (Фаза 12, B4; F5-persist — по образцу ?pin=/?token=). Мусор/выход за диапазон
 * → 1; жёсткий потолок защищает от дикого offset к API.
 */
function readHistoryPage(): number {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') return 1;
  const raw = Number(new URLSearchParams(window.location.search).get('page'));
  if (!Number.isFinite(raw)) return 1;
  return Math.min(Math.max(Math.trunc(raw), 1), 9999);
}

export default function App(): JSX.Element {
  // AuthProvider поднимаем на самый верх: bootstrap /me один раз на старте,
  // дочерние экраны и гейт /edit получают user/loading через useAuth().
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

/** Внутренний диспетчер: имеет доступ к useAuth() (внутри провайдера). */
function AppInner(): JSX.Element {
  const pathname = useRoute();
  const { user, loading, signOut } = useAuth();
  // Канарейка i18n (И1): подвальная навигация аккаунта берётся из каталога
  // `ru/common.json` через `t('nav.*')`. Доказывает работоспособность цепочки
  // react-i18next; полный перенос строк — И2.
  const { t } = useTranslation();
  // Slug публичного открытого квиза (Фаза 10, И11): /q/<slug> → OpenQuizRun
  // (вычисляется один раз — рендер и redirect-гейт ниже читают одну проверку).
  const openSlug = matchOpenRoute(pathname);

  const [screen, setScreen] = useState<Screen>(() => {
    // Session-resume: активная сессия из sessionStorage переживает F5 (см. activeSession.ts).
    const host = loadHost();
    if (host) return { kind: 'host' };
    const player = loadPlayer();
    if (player) return { kind: 'player', pin: player.pin };
    const pin = readPinFromQuery();
    return pin.length === PIN_LENGTH ? { kind: 'player', pin } : { kind: 'home' };
  });
  const [loggingOut, setLoggingOut] = useState(false);
  // gameId для правки существующего квиза из личного кабинета (И4): Dashboard
  // «Редактировать» → setEditingGameId + navigate('/edit'). С Фазы 12 (B1) это
  // только РАЗОВЫЙ deeplink «открой правку этой игры»: сам PUT/POST решается по
  // персистентной привязке draft.gameId в localStorage, поэтому привязка не
  // теряется при выходе и прямом заходе в /edit (дубли квизов не плодятся).
  // Сбрасывается при уходе с /edit (повторный визит — обычный вход в редактор).
  const [editingGameId, setEditingGameId] = useState<number | null>(null);

  // Неизвестный путь → редирект на главную (replaceState, без новой записи в истории).
  useEffect(() => {
    if (!isKnownRoute(pathname)) {
      redirect('/');
    }
  }, [pathname]);

  // Гейт защищённых роутов /edit и /me: без залогиненности — редирект на /login.
  // Ждём завершения bootstrap (loading), чтобы не мигать редиректом при первом
  // рендере. Defense-in-depth против гонки пост-логин навигации: рендер смены
  // роута на /me может выполниться со старым user=null (апдейт контекста из
  // signIn коммитится следующим рендером) — мгновый redirect откатил бы
  // pushState('/me') обратно на /login. Поэтому перед откатом ждём один кадр
  // и перепроверяем user по ref: за это время апдейт гарантированно применяется.
  // (Парный фикс — Login.tsx: onSuccess зовётся из эффекта при появлении user.)
  const userRef = useRef(user);
  userRef.current = user;
  useEffect(() => {
    if (pathname !== '/edit' && pathname !== '/me' && pathname !== '/me/history') return;
    if (loading || user !== null) return;
    let cancelled = false;
    // rAF отсутствует в jsdom без pretendToBeVisual → фолбэк на setTimeout(0).
    const schedule: (cb: () => void) => number =
      typeof window.requestAnimationFrame === 'function'
        ? (cb) => window.requestAnimationFrame(cb)
        : (cb) => window.setTimeout(cb, 0);
    const cancel: (id: number) => void =
      typeof window.cancelAnimationFrame === 'function'
        ? (id) => window.cancelAnimationFrame(id)
        : (id) => window.clearTimeout(id);
    const id = schedule(() => {
      if (!cancelled && userRef.current === null) redirect('/login');
    });
    return () => {
      cancelled = true;
      cancel(id);
    };
  }, [pathname, loading, user]);

  // Гейт админ-роута /admin (Фаза 7, И1): только аутентифицированный администратор.
  // Аноним или обычный хост (role==='user') → редирект на главную. Ждём bootstrap,
  // чтобы не мигать редиректом залогиненного админа при первом рендере. Backend
  // /api/admin/* дополнительно закрыт RequireAdmin (defense-in-depth, 403).
  useEffect(() => {
    if (pathname === '/admin' && !loading && user?.role !== 'admin') {
      redirect('/');
    }
  }, [pathname, loading, user]);

  // Сброс deeplink'а правки при уходе с /edit (вернулись в кабинет/на главную),
  // чтобы повторный визит в редактор открывался обычным списком черновиков.
  // PUT-привязка при этом НЕ теряется — она персистентна в draft.gameId (B1).
  useEffect(() => {
    if (pathname !== '/edit' && editingGameId !== null) {
      setEditingGameId(null);
    }
  }, [pathname, editingGameId]);

  async function handleSignOut(): Promise<void> {
    setLoggingOut(true);
    try {
      await signOut();
    } finally {
      setLoggingOut(false);
    }
  }

  // Auth-экраны рисуют собственную навигацию; на них подвальный блок аккаунта не нужен.
  const isAuthScreen =
    pathname === '/login' ||
    pathname === '/register' ||
    pathname === '/forgot' ||
    pathname === '/reset';
  const showFooter = isKnownRoute(pathname) && !isAuthScreen;
  // Неблокирующий баннер «подтвердите email»: залогинен, но verified=false (И3).
  // Не показываем на auth-экранах и на самом /verify (там своя обработка подтверждения).
  const showBanner = user !== null && !user.verified && showFooter && pathname !== '/verify';

  // Ширина топбара = ширина основной карточки маршрута: контролы темы/a11y должны быть
  // вровень с ПРАВЫМ краем визуального блока. Главная — .card (460); host/player,
  // кабинет/админка — card--wide (720); редактор /edit — card--editor (960, а на
  // широких экранах 1200/1320 — Фаза 12, D1, модификатор --editor); остальные
  // (auth/контент) — card--page (640).
  const topbarClass =
    pathname === '/' && screen.kind === 'home'
      ? 'app-topbar app-topbar--narrow'
      : pathname === '/edit'
        ? 'app-topbar app-topbar--editor'
        : pathname === '/' ||
            pathname === '/me' ||
            pathname === '/me/history' ||
            pathname === '/admin'
          ? 'app-topbar app-topbar--wide'
          : 'app-topbar';

  // Обёртка .app-shell по умолчанию 720px — для /edit этого мало: карточка
  // редактора шире (.card--editor 960/1200/1320, Фаза 12 D1), и без модификатора
  // обёртка обрезала её обратно до 720px. Модификатор --editor расширяет обёртку
  // синхронно с карточкой и топбаром --editor (media-query в index.css).
  const shellClass = pathname === '/edit' ? 'app-shell app-shell--editor' : 'app-shell';

  return (
    <div className="page">
      <div className={shellClass}>
        {/* Шапка приложения (Фаза 9, П1+П2): flex-контейнер вверху основного блока.
            Раньше переключатель тем «висел» position:fixed в углу страницы (поверх
            контента, перекрывая текст/кнопки на мобильных); теперь он встроен в
            шапку внутри .app-shell. Тема по-прежнему применяется инлайн-скриптом
            в index.html до рендера; здесь лишь UI + сохранение в localStorage.
            П2: рядом с ThemeToggle добавлен accessibility-значок (<A11yPanel/>),
            открывающий popover с гранулярными настройками доступности
            (размер шрифта, контраст, анимация, шрифт для дислексии, подчёркивание
            ссылок, сброс) — каждая ставит свой data-a11y-* на <html> (инлайн-скрипт
            применяет их до рендера, без вспышки). justify-content: flex-end
            прижимает виджеты к правому краю; A11yPanel слева, ThemeToggle справа
            (как в прежней угловой позиции). При появлении левого контента
            (логотип/заголовок) достаточно сменить на space-between. */}
        <div className={topbarClass}>
          <A11yPanel />
          <ThemeToggle />
        </div>
        {showBanner ? <UnverifiedBanner /> : null}
        {pathname === '/' && (
          <>
            {screen.kind === 'home' ? (
              <HomeScreen
                onHost={() => setScreen({ kind: 'host' })}
                onJoin={(pin) => setScreen({ kind: 'player', pin })}
              />
            ) : null}

            {screen.kind === 'host' ? (
              <HostScreen
                onExit={() => setScreen({ kind: 'home' })}
                onOpenEditor={() => navigate('/edit')}
              />
            ) : null}

            {screen.kind === 'player' ? (
              <PlayerScreen pin={screen.pin} onExit={() => setScreen({ kind: 'home' })} />
            ) : null}
          </>
        )}

        {pathname === '/about' ? <About /> : null}
        {pathname === '/games' ? <Games /> : null}

        {/* Публичный соло-экран открытого квиза `/q/<slug>` (Фаза 10, И11): без
            auth-гейта — залогиненность не требуется и не предлагается; экран
            самодостаточен (мета → ник → свайп-дека → финиш+reveal), noindex
            ставит сам. Диспетчер до гейтов: /q/ не пересекается с /me//edit. */}
        {openSlug !== null ? <OpenQuizRun slug={openSlug} /> : null}

        {pathname === '/login' ? <Login onSuccess={() => navigate('/me')} /> : null}
        {pathname === '/register' ? <Register onSuccess={() => navigate('/')} /> : null}
        {pathname === '/forgot' ? <Forgot /> : null}
        {pathname === '/reset' ? (
          <Reset token={readResetToken()} onSuccess={() => navigate('/login')} />
        ) : null}
        {pathname === '/verify' ? (
          <Verify token={readVerifyToken()} onSuccess={() => navigate('/')} />
        ) : null}
        {pathname === '/privacy' ? <Privacy /> : null}

        {pathname === '/edit' ? (
          // Гейт: редактор монтируем только для залогиненного пользователя; пока
          // идёт bootstrap либо пользователя нет — показываем понятную заглушку
          // (редирект на /login стартует в эффекте выше после завершения bootstrap).
          user !== null ? (
            // Публикация непривязанного черновика («Создать», 0.12.1) после
            // успешного POST уходит в кабинет /me: квиз появляется в «Мои
            // квизы», проведение — кнопкой «Провести» там же (раньше редактор
            // сразу запускал host-флоу сессией).
            <HostEditor
              onPublished={() => navigate('/me')}
              onExit={() => {
                // Вошли в правку из кабинета (deeplink) — возврат в /me (И4);
                // иначе — главная. PUT-привязка черновика сохранится в localStorage
                // (draft.gameId, B1) и переживёт выход/повторный заход.
                if (editingGameId != null) {
                  setEditingGameId(null);
                  navigate('/me');
                } else {
                  navigate('/');
                }
              }}
              editGameId={editingGameId}
            />
          ) : (
            <main className="card card--page">
              <p className="muted" data-testid="edit-gate">
                {loading ? t('common.loading') : t('app.editGateGuest')}
              </p>
            </main>
          )
        ) : null}

        {pathname === '/me' ? (
          // Личный кабинет (И4): гейт auth обрабатывается и эффектом выше, и внутри
          // Dashboard (redirect на /login при отсутствии пользователя). onLaunchGame
          // запускает host-флоу по выбранному квизу (как из HostSelect); onEditGame —
          // разовый deeplink правки (editingGameId): редактор переиспользует/создаст
          // черновик с персистентной привязкой gameId (B1).
          <Dashboard
            // Сессия уже создана в Dashboard (startSession) и сохранена через saveHost
            // (activeSession): HostScreen подхватит её через loadHost() при mount и
            // сразу перейдёт к HostSession, минуя повторный startSession (дубль комнаты
            // привёл бы к ложному limit_parallel_rooms). pendingLaunch НЕ задаём.
            onLaunchGame={() => {
              setScreen({ kind: 'host' });
              navigate('/');
            }}
            onEditGame={(gameId) => {
              setEditingGameId(gameId);
              navigate('/edit');
            }}
            // «Открыть» активную сессию: saveHost уже вызван в Dashboard (resume),
            // HostScreen подхватит {pin, sessionId} через loadHost() при mount.
            onResumeSession={() => {
              setScreen({ kind: 'host' });
              navigate('/');
            }}
          />
        ) : null}

        {pathname === '/me/history' ? (
          // Полная история сессий (Фаза 12, B4): гейт auth — в эффекте выше (как
          // для /me); номер страницы приходит из ?page= (F5-persist), дальше экран
          // сам синхронизирует URL при листании (navigate /me/history?page=N).
          <SessionHistory initialPage={readHistoryPage()} />
        ) : null}

        {pathname === '/admin' ? (
          // Админ-дашборд (Фаза 7, И1). Гейт role==='admin' — в эффекте выше
          // (не-админ → redirect '/'); экран самодостаточно проверяет useAuth.
          <Admin />
        ) : null}

        {!isKnownRoute(pathname) ? (
          <main className="card card--page">
            <p className="muted">{t('app.redirectingHome')}</p>
          </main>
        ) : null}
      </div>

      {showFooter ? (
        <footer className="footer">
          <div className="footer-nav">
            {user ? (
              <span className="footer-auth" data-testid="footer-auth">
                <span className="footer-auth__email">{user.email}</span>
                <Link to="/me" className="footer-link">
                  {t('nav.dashboard')}
                </Link>
                {user.role === 'admin' ? (
                  <Link to="/admin" className="footer-link" data-testid="nav-admin">
                    {t('nav.admin')}
                  </Link>
                ) : null}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => void handleSignOut()}
                  disabled={loggingOut}
                  data-testid="logout-btn"
                >
                  {loggingOut ? t('nav.logoutInProgress') : t('nav.logout')}
                </button>
              </span>
            ) : (
              <span className="footer-auth" data-testid="footer-auth">
                <Link to="/login" className="footer-link">
                  {t('nav.login')}
                </Link>
                <Link to="/register" className="footer-link">
                  {t('nav.register')}
                </Link>
              </span>
            )}
            <Link to="/about" className="footer-link">
              {t('nav.about')}
            </Link>
          </div>
          {/* Переключатель языка (Фаза 4, И6): поддомен = источник истины, клик —
              навигация на en./cn./apex. Вынесен на отдельную строку футера (не в
              ряду с Войти/Регистрация); виден на основных экранах. */}
          <LanguageSwitcher />
        </footer>
      ) : null}
    </div>
  );
}
